import { html, nothing } from 'lit';
import { sanitizeHttpUrl } from './url-sanitizer';

export type SourceIconKind =
  | 'gmail'
  | 'slack'
  | 'messenger'
  | 'facebook'
  | 'chatwork'
  | 'calendar'
  | 'notion'
  | 'github'
  | 'linkedin'
  | 'google_docs'
  | 'google_sheets'
  | 'freee'
  | 'screenshot'
  | 'manual'
  | 'web'
  | 'generic';

export interface SourceIconDescriptor {
  kind: SourceIconKind;
  label: string;
  asset?: string;
  materialIcon: string;
}

const BRAND_ASSETS: Partial<Record<SourceIconKind, { label: string; file: string }>> = {
  gmail: { label: 'Gmail', file: 'gmail.svg' },
  slack: { label: 'Slack', file: 'slack.svg' },
  messenger: { label: 'Messenger', file: 'messenger.svg' },
  facebook: { label: 'Facebook', file: 'facebook.svg' },
  chatwork: { label: 'Chatwork', file: 'chatwork.svg' },
  calendar: { label: 'Google Calendar', file: 'google-calendar.svg' },
  notion: { label: 'Notion', file: 'notion.png' },
  github: { label: 'GitHub', file: 'github.svg' },
  linkedin: { label: 'LinkedIn', file: 'linkedin.png' },
  google_docs: { label: 'Google Docs', file: 'google-docs.svg' },
  google_sheets: { label: 'Google Sheets', file: 'google-sheets.svg' },
  freee: { label: 'freee', file: 'freee.png' },
};

const FALLBACKS: Record<SourceIconKind, { label: string; materialIcon: string }> = {
  gmail: { label: 'Gmail', materialIcon: 'mail' },
  slack: { label: 'Slack', materialIcon: 'forum' },
  messenger: { label: 'Messenger', materialIcon: 'chat' },
  facebook: { label: 'Facebook', materialIcon: 'groups' },
  chatwork: { label: 'Chatwork', materialIcon: 'chat' },
  calendar: { label: 'Google Calendar', materialIcon: 'calendar_month' },
  notion: { label: 'Notion', materialIcon: 'article' },
  github: { label: 'GitHub', materialIcon: 'code' },
  linkedin: { label: 'LinkedIn', materialIcon: 'business_center' },
  google_docs: { label: 'Google Docs', materialIcon: 'description' },
  google_sheets: { label: 'Google Sheets', materialIcon: 'table_chart' },
  freee: { label: 'freee', materialIcon: 'receipt_long' },
  screenshot: { label: 'Screenshot', materialIcon: 'photo_camera' },
  manual: { label: 'Text', materialIcon: 'notes' },
  web: { label: 'Web page', materialIcon: 'language' },
  generic: { label: 'Source', materialIcon: 'link' },
};

function hostMatches(hostname: string, domain: string) {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

export function resolveSourceIconKind(sourceUrl?: unknown, sourceKind?: unknown): SourceIconKind {
  const safeUrl = sanitizeHttpUrl(sourceUrl);
  if (safeUrl) {
    try {
      const url = new URL(safeUrl);
      const host = url.hostname.toLowerCase();
      const path = url.pathname.toLowerCase();

      if (host === 'mail.google.com' || host === 'gmail.com') return 'gmail';
      if (host === 'calendar.google.com') return 'calendar';
      if (host === 'docs.google.com' && path.startsWith('/document')) return 'google_docs';
      if (host === 'docs.google.com' && path.startsWith('/spreadsheets')) return 'google_sheets';
      if (hostMatches(host, 'slack.com')) return 'slack';
      if (hostMatches(host, 'messenger.com')) return 'messenger';
      if (hostMatches(host, 'facebook.com')) return path.startsWith('/messages') ? 'messenger' : 'facebook';
      if (hostMatches(host, 'chatwork.com') || host === 'kcw.kddi.ne.jp') return 'chatwork';
      if (hostMatches(host, 'notion.so') || hostMatches(host, 'notion.site')) return 'notion';
      if (host === 'github.com') return 'github';
      if (hostMatches(host, 'linkedin.com')) return 'linkedin';
      if (hostMatches(host, 'freee.co.jp')) return 'freee';
    } catch {
      // Fall through to source_kind and then generic fallback.
    }
  }

  if (typeof sourceKind === 'string') {
    const normalized = sourceKind.toLowerCase();
    if (normalized === 'gmail') return 'gmail';
    if (normalized === 'slack') return 'slack';
    if (normalized === 'messenger') return 'messenger';
    if (normalized === 'facebook') return 'facebook';
    if (normalized === 'chatwork') return 'chatwork';
    if (normalized === 'calendar' || normalized === 'google_calendar') return 'calendar';
    if (normalized === 'notion') return 'notion';
    if (normalized === 'github') return 'github';
    if (normalized === 'linkedin') return 'linkedin';
    if (normalized === 'google_docs' || normalized === 'google-docs') return 'google_docs';
    if (normalized === 'google_sheets' || normalized === 'google-sheets') return 'google_sheets';
    if (normalized === 'freee') return 'freee';
    if (normalized === 'google-calendar') return 'calendar';
    if (normalized === 'sync_event_auto' || normalized.startsWith('calendar_') || normalized.includes('google_calendar')) return 'calendar';
    if (normalized.startsWith('gmail')) return 'gmail';
    if (normalized.startsWith('slack')) return 'slack';
    if (normalized.includes('messenger') || normalized === 'facebook_messages' || normalized === 'facebook-messages') return 'messenger';
    if (normalized === 'screenshot') return 'screenshot';
    if (normalized === 'manual') return 'manual';
    if (normalized === 'web') return 'web';
  }

  return safeUrl ? 'web' : 'generic';
}

export function resolveSourceIcon(sourceUrl?: unknown, sourceKind?: unknown): SourceIconDescriptor {
  const kind = resolveSourceIconKind(sourceUrl, sourceKind);
  const fallback = FALLBACKS[kind];
  const brand = BRAND_ASSETS[kind];
  return {
    kind,
    label: brand?.label || fallback.label,
    asset: brand ? `/assets/source-icons/${brand.file}` : undefined,
    materialIcon: fallback.materialIcon,
  };
}

export function renderSourceIcon(sourceUrl?: unknown, sourceKind?: unknown, size: 'xs' | 'sm' = 'sm') {
  const icon = resolveSourceIcon(sourceUrl, sourceKind);
  if (icon.asset) {
    return html`<img class=${`source-kind-icon source-kind-icon-${size}`} src=${icon.asset} alt=${icon.label}>`;
  }
  return html`<span class=${`mi mi-${size}`} role="img" aria-label=${icon.label}>${icon.materialIcon}</span>`;
}

export function renderOptionalSourceIcon(sourceUrl?: unknown, sourceKind?: unknown, size: 'xs' | 'sm' = 'sm') {
  if (!sourceUrl && !sourceKind) return nothing;
  return renderSourceIcon(sourceUrl, sourceKind, size);
}
